import { lazy, Suspense } from "react";
import { ThemeProvider } from "./context/ThemeContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AdminLockScreen } from "./components/admin/AdminLockScreen";
import { AdminDevicesPage } from "./pages/admin/AdminDevicesPage";
import { AdminBroadcastPage } from "./pages/admin/AdminBroadcastPage";
import { StudentProvider } from "./context/StudentContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthLayout } from "./layouts/AuthLayout";
import { StudentLayout } from "./layouts/StudentLayout";
import { AdminLayout } from "./layouts/AdminLayout";
import { LandingPage } from "./pages/LandingPage";
import { AdminLoginPage } from "./pages/auth/AdminLoginPage";
import { AuthPage } from "./pages/auth/AuthPages";
import { StudentDashboard } from "./pages/student/Dashboard";
import { ProfilePage, SettingsPage } from "./pages/student/Profile";
import { HelpPage } from "./pages/student/HelpPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { LegacyExamRedirect } from "./pages/student/LegacyExamRedirect";
import {
  StudySubjectPicker,
  StudyModulesList,
  StudySubjectQuiz,
  StudySubjectExams,
  StudySubjectNotes,
  StudyExamPractice,
  StudyQuizPlay,
  StudyQuizResults,
} from "./pages/student/StudyHub";
import { ShortNotesReaderPage } from "./pages/student/ShortNotesReaderPage";
import { CoursePage } from "./pages/student/CoursePage";
import { QuizHubPage, ExamHubPage } from "./pages/student/QuizHubPage";

// Heavy screens load only when opened: the chapter pages (quiz), the notes reader (KaTeX, figures), downloads and the PDF viewer (PDF.js).
const ChapterListPage = lazy(() => import("./pages/student/ChapterPages").then((m) => ({ default: m.ChapterListPage })));
const ChapterPage = lazy(() => import("./pages/student/ChapterPages").then((m) => ({ default: m.ChapterPage })));
const ChapterNotesReaderPage = lazy(() => import("./pages/student/ChapterNotesReaderPage").then((m) => ({ default: m.ChapterNotesReaderPage })));
const DownloadsPage = lazy(() => import("./pages/student/DownloadsPage").then((m) => ({ default: m.DownloadsPage })));
const ModuleViewerPage = lazy(() => import("./pages/student/ModuleViewerPage"));
import { GpaCalculatorPage } from "./pages/student/GpaCalculator";
import { CocExamsPage, CocExamPlayPage } from "./pages/student/CocExamsPage";
import { SemesterCoursesPage } from "./pages/student/SemesterCoursesPage";
import { PremiumPage } from "./pages/student/PremiumPage";
import { AdminCocPage } from "./pages/admin/AdminCocPage";
import { AdminIngestReviewPage } from "./pages/admin/AdminIngestReviewPage";
import {
  AdminOverview, AdminUsers, AdminResources,
  AdminPayments, AdminAnalytics, AdminSettings, AdminLogs,
} from "./pages/admin/AdminPages";
import { AdminCatalog } from "./pages/admin/AdminCatalog";
import { AdminAdmins } from "./pages/admin/AdminAdmins";
import { AdminReviewPage } from "./pages/admin/AdminReviewPage";
import { AdminResourceDetailPage } from "./pages/admin/AdminResourceDetailPage";
import { AdminCoursesPage } from "./pages/admin/AdminCoursesPage";
import { AdminCourseDetailPage } from "./pages/admin/AdminCourseDetailPage";
import { AdminManualExtractionPage } from "./pages/admin/AdminManualExtractionPage";
import { AdminPackagesPage } from "./pages/admin/AdminPackagesPage";

import { ToastProvider } from "./components/ui/Toast";
import { PWAUpdateManager } from "./components/pwa/PWAUpdateManager";

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <PWAUpdateManager />
          <AdminLockScreen />
          <BrowserRouter>
            <ErrorBoundary>
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<LandingPage />} />

            <Route path="/auth" element={<AuthLayout />}>
              <Route index element={<AuthPage />} />
              <Route path="login" element={<AuthPage />} />
              <Route path="register" element={<AuthPage />} />
              <Route path="admin" element={<AdminLoginPage />} />
              <Route path="verify" element={<Navigate to="/auth" replace />} />
            </Route>

            <Route
              path="/student"
              element={
                <ProtectedRoute role="student">
                  <StudentProvider><StudentLayout /></StudentProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<StudentDashboard />} />

              <Route path="study" element={<StudySubjectPicker />} />
              <Route path="study/semester" element={<Navigate to="/student/study" replace />} />
              <Route path="study/semester/:semesterNumber" element={<SemesterCoursesPage />} />
              <Route path="quiz" element={<QuizHubPage />} />
              <Route path="study/gpa" element={<GpaCalculatorPage />} />
              <Route path="gpa" element={<GpaCalculatorPage />} />
              <Route path="study/quiz/play" element={<StudyQuizPlay />} />
              <Route path="study/quiz/results" element={<StudyQuizResults />} />
              <Route path="study/:subject" element={<CoursePage />} />
              <Route path="study/:subject/modules" element={<StudyModulesList />} />
              <Route path="study/:subject/modules/:moduleId" element={<StudyModulesList />} />
              <Route path="study/:subject/quiz" element={<StudySubjectQuiz />} />
              <Route path="study/:subject/exams" element={<StudySubjectExams />} />
              <Route path="study/:subject/exams/:examId" element={<StudyExamPractice />} />
              <Route path="study/:subject/chapters" element={<ChapterListPage />} />
              <Route path="study/:subject/chapters/:chapterId" element={<ChapterPage />} />
              <Route path="study/:subject/chapters/:chapterId/notes" element={<ChapterNotesReaderPage />} />
              <Route path="view/:moduleId" element={<ModuleViewerPage />} />
              <Route path="downloads" element={<DownloadsPage />} />
              <Route path="study/:subject/notes" element={<StudySubjectNotes />} />
              <Route path="study/:subject/notes/:topicId" element={<ShortNotesReaderPage />} />
              <Route path="notes/:topicId" element={<ShortNotesReaderPage />} />

              <Route path="coc" element={<CocExamsPage />} />
              <Route path="coc/:cocId" element={<CocExamPlayPage />} />

              <Route path="premium" element={<PremiumPage />} />

              <Route path="modules" element={<Navigate to="/student/study" replace />} />
              <Route path="exams" element={<ExamHubPage />} />
              <Route path="exams/:examId" element={<LegacyExamRedirect />} />
              <Route path="ai-tools" element={<Navigate to="/student/study" replace />} />
              <Route path="ai-tools/quiz" element={<Navigate to="/student/study/quiz/play" replace />} />
              <Route path="ai-tools/quiz/results" element={<Navigate to="/student/study/quiz/results" replace />} />

              <Route path="profile" element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="help" element={<HelpPage />} />
            </Route>

            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="resources" element={<AdminResources />} />
              <Route path="resources/:resourceId" element={<AdminResourceDetailPage />} />
              <Route path="courses" element={<AdminCoursesPage />} />
              <Route path="courses/:courseId" element={<AdminCourseDetailPage />} />
              <Route path="manual-extraction" element={<AdminManualExtractionPage />} />
              <Route path="review" element={<Navigate to="/admin/courses" replace />} />
              <Route path="coc" element={<AdminCocPage />} />
              <Route path="content-review" element={<AdminIngestReviewPage />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="packages" element={<AdminPackagesPage />} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="devices" element={<AdminDevicesPage />} />
              <Route path="broadcast" element={<AdminBroadcastPage />} />
              <Route path="admins" element={<AdminAdmins />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  </ThemeProvider>
  );
}
